import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SubHeader from 'components/common/SubHeader';
import ReviewCard from 'components/review/ReviewCard';
import CommonModal from 'components/common/CommonModal';
import NoData from 'components/common/NoData';
import { useSnackbar } from 'contexts/SnackbarContext';
import { getMyReviews, deleteReview } from 'api/review';
import 'assets/scss/pages/Review.scss';

const MyReviews = () => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useSnackbar();

    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchReviews = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getMyReviews();
            setReviews(data.results || data || []);
        } catch {
            showError('리뷰 목록을 불러오지 못했습니다');
        } finally {
            setLoading(false);
        }
    }, [showError]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const handleEdit = (review) => {
        navigate(`/review/edit/${review.id}`);
    };

    const handleDeleteRequest = (reviewId) => {
        setDeleteTargetId(reviewId);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTargetId) return;
        setDeleting(true);
        try {
            await deleteReview(deleteTargetId);
            setReviews((prev) => prev.filter((r) => r.id !== deleteTargetId));
            showSuccess('리뷰가 삭제되었습니다');
        } catch {
            showError('리뷰 삭제에 실패했습니다');
        } finally {
            setDeleting(false);
            setDeleteTargetId(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteTargetId(null);
    };

    return (
        <div className="page-my-reviews">
            <SubHeader title="내 리뷰" />

            <div className="page-my-reviews__body">
                {loading ? (
                    <div className="page-my-reviews__loading">불러오는 중...</div>
                ) : reviews.length === 0 ? (
                    <NoData
                        title="작성한 리뷰가 없어요"
                        desc="구매한 상품에 리뷰를 남겨보세요"
                    />
                ) : (
                    <div className="page-my-reviews__list">
                        {reviews.map((review) => (
                            <ReviewCard
                                key={review.id}
                                review={review}
                                onEdit={handleEdit}
                                onDelete={handleDeleteRequest}
                            />
                        ))}
                    </div>
                )}
            </div>

            <CommonModal
                isOpen={!!deleteTargetId}
                onRequestClose={handleDeleteCancel}
                title="리뷰 삭제"
            >
                <div className="page-my-reviews__modal-body">
                    <p>리뷰를 삭제하면 복구할 수 없습니다.<br />정말 삭제하시겠습니까?</p>
                    <div className="page-my-reviews__modal-actions">
                        <button
                            type="button"
                            className="page-my-reviews__modal-cancel"
                            onClick={handleDeleteCancel}
                            disabled={deleting}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            className="page-my-reviews__modal-confirm"
                            onClick={handleDeleteConfirm}
                            disabled={deleting}
                        >
                            {deleting ? '삭제 중...' : '삭제'}
                        </button>
                    </div>
                </div>
            </CommonModal>
        </div>
    );
};

export default MyReviews;
